import LandingSubText from '../ui/landing-sub-text';
import Link from 'next/link';
import DynamicIcon from '../ui/dynamic-icon';
import { useState } from 'react';
import { useRouter } from 'next/router';
import LandingPageHeader from '../ui/landing-page-header';
import LandingPageContainer from '../ui/landing-page-container';
import LandingHeroHeading from '../ui/landing-hero-heading';
import LandingPageButton from '../ui/landing-page-button';
import Landing from '../../pages';
import LandingHeadingText from '../ui/landing-heading-text';
import { prefix } from '../../lib/auth';
import HeroBackground from '../ui/hero-background.js';
import copy from "copy-to-clipboard";
import {
    IconCodeblock, IconTemplate, IconNavigation,
    IconPlug, IconTable, IconCode, IconBrandPython,
    IconCloudBolt, IconLayoutGrid, IconBrandGitlab,
    IconCopy, IconCheck
} from '@tabler/icons-react';

const LandingPage = ({ children }) => {
    const router = useRouter();
    const [activeStep, setActiveStep] = useState(1);
    const [copiedCommand, setCopiedCommand] = useState(null);

    const handleCopyCommand = (command, stepId) => {
        copy(command);
        setCopiedCommand(stepId);
        setTimeout(() => {
            setCopiedCommand(null);
        }, 2000);
    }

    const everythingYouNeedData = [
        {
            id: 1,
            icon: <IconCodeblock stroke={1.5} size={32} />,
            title: "Getting Started",
            description: "Scaffold, compile, and serve a Peg UI app with live reload in one command.",
            url: "/docs/latest/getting-started/"
        },
        {
            id: 2,
            icon: <IconTemplate stroke={1.5} size={32} />,
            title: "Widgets",
            description: "The full layout, display, interactive, and form widget set.",
            url: "/docs/latest/widgets/"
        },
        {
            id: 3,
            icon: <IconNavigation stroke={1.5} size={32} />,
            title: "Routing & navigation",
            description: "Multi-page apps with Router and Route.",
            url: "/docs/latest/routing/"
        },
        {
            id: 4,
            icon: <IconPlug stroke={1.5} size={32} />,
            title: "Data & realtime",
            description: "Loading data from a backend, sockets, and server-sent events.",
            url: "/docs/latest/data-realtime/"
        },
        {
            id: 5,
            icon: <IconTable stroke={1.5} size={32} />,
            title: "Tables & Rows",
            description: "The platform's structured-data store: columns, permissions, namespaces.",
            url: "/docs/latest/tables/"
        },
        {
            id: 6,
            icon: <IconCode stroke={1.5} size={32} />,
            title: "Functions",
            description: "Server-side hooks - scaffold, build, run, and submit from the CLI.",
            url: "/docs/latest/functions/"
        },
        {
            id: 7,
            icon: <IconBrandPython stroke={1.5} size={32} />,
            title: "Python UI",
            description: "Build serverless logic, run locally, package and publish.",
            url: "/docs/latest/schema-ui/"
        },
        {
            id: 8,
            icon: <IconCloudBolt stroke={1.5} size={32} />,
            title: "Assets",
            description: "Manage CDN assets & versions alongside your projects.",
            url: "/docs/latest/cdn/"
        },
        {
            id: 9,
            icon: <IconLayoutGrid stroke={1.5} size={32} />,
            title: "Pegs",
            description: "Package reusable capabilities and distribute them.",
            url: "/docs/latest/architecture/"
        }

    ]

    const installationsteps = [
        {
            id: 1,
            step: "01",
            title: "Install the SDK",
            description: "The SDK is pre-1.0 - pin your minor version in anything you deploy.",
            command: "pip install workpeg"
        },
        {
            id: 2,
            step: "02",
            title: "Scaffold a Peg UI app",
            description: "One command creates the project, compiles it, and serves it with live reload.",
            command: "workpeg ui new counter"
        },
        {
            id: 3,
            step: "03",
            title: "Add a Function",
            description: "Scaffold backend logic, a pre-/post-save hook alongside your UI.",
            command: "workpeg function new hello"
        },
        {
            id: 4,
            step: "04",
            title: "Submit and publish",
            description: "Build and submit your Function, then publish assets to the CDN -full detail is in the docs.",
            cta: true
        }
    ]

    const codeTokenColors = {
        kw: 'text-[#C586C0]',
        type: 'text-[#4EC9B0]',
        func: 'text-[#DCDCAA]',
        self: 'text-[#9CDCFE]',
        str: 'text-[#CE9178]',
        num: 'text-[#B5CEA8]',
        plain: 'text-[#D4D4D4]'
    }

    const codePreviewLines = [
        [
            { text: 'from ', color: 'kw' },
            { text: 'workpeg.ui', color: 'self' },
            { text: ' import ', color: 'kw' },
            { text: 'App', color: 'type' },
            { text: ', ', color: 'plain' },
            { text: 'State', color: 'type' },
            { text: ', ', color: 'plain' },
            { text: 'Column', color: 'type' },
            { text: ', ', color: 'plain' },
            { text: 'Text', color: 'type' },
            { text: ', ', color: 'plain' },
            { text: 'Button', color: 'type' }
        ],
        [],
        [
            { text: 'class ', color: 'kw' },
            { text: 'CounterPage', color: 'type' },
            { text: '(App):', color: 'plain' }
        ],
        [
            { text: '    count = ', color: 'plain' },
            { text: 'State', color: 'type' },
            { text: '(', color: 'plain' },
            { text: '0', color: 'num' },
            { text: ')', color: 'plain' }
        ],
        [],
        [
            { text: '    def ', color: 'kw' },
            { text: 'increment', color: 'func' },
            { text: '(', color: 'plain' },
            { text: 'self', color: 'self' },
            { text: '):', color: 'plain' }
        ],
        [
            { text: '        ', color: 'plain' },
            { text: 'self', color: 'self' },
            { text: '.count += ', color: 'plain' },
            { text: '1', color: 'num' }
        ],
        [],
        [
            { text: '    def ', color: 'kw' },
            { text: 'build', color: 'func' },
            { text: '(', color: 'plain' },
            { text: 'self', color: 'self' },
            { text: '):', color: 'plain' }
        ],
        [
            { text: '        ', color: 'plain' },
            { text: 'return ', color: 'kw' },
            { text: 'Column', color: 'type' },
            { text: '(', color: 'plain' }
        ],
        [
            { text: '            ', color: 'plain' },
            { text: 'Text', color: 'type' },
            { text: '(f"Count: {self.count}"),', color: 'str' }
        ],
        [
            { text: '            ', color: 'plain' },
            { text: 'Button', color: 'type' },
            { text: '(', color: 'plain' },
            { text: '"Increment"', color: 'str' },
            { text: ', on_click=', color: 'plain' },
            { text: 'self', color: 'self' },
            { text: '.increment),', color: 'plain' }
        ],
        [
            { text: '        )', color: 'plain' }
        ]
    ]

    return (
        <div className='h-screen w-screen overflow-y-auto scrollbar-none lg:custom-scrollbar relative bg-[#000000] !font-geist' style={{ scrollBehavior: 'smooth' }}>
            <div className='border-b border-[#1C1C1C] bg-[#00000080] sticky top-0 z-50' style={{ backdropFilter: 'blur(20px)' }}>
                <LandingPageHeader />
            </div>
            {/* hero section */}
            <div className='max-w-[1300px] lg:border-x-[1px] border-b-[1px] border-[#1C1C1C] mx-auto'>
                <HeroBackground>
                    <div className='w-full '>
                        <LandingPageContainer>
                            <div className='relative grid items-center w-fit mx-auto h-[475px] lg:h-[644px] max-h-[calc(100dvh-70px)]'>
                                <div
                                    aria-hidden
                                    className='pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2
                                    w-[345px] lg:w-[643px] h-[300px] lg:h-[417px] rounded-full bg-[#000000]/80 blur-[75px]'
                                />

                                <div className='relative grid gap-[29px] lg:gap-[32px] px-[10px] lg:px-[19.5px]'>
                                    <div className='grid gap-[12px] lg:gap-[16px]'>
                                        <LandingHeroHeading additionalclasses='max-w-[604px] m-auto'>Build Pegs for Workpeg</LandingHeroHeading>
                                        <LandingSubText additionalclasses='max-w-[485px] m-auto'>
                                            Apps that run inside businesses&apos; workspaces, with data, files and automation already wired up. Python in, live app out.
                                        </LandingSubText>
                                    </div>
                                    <div className='flex items-center gap-[12px] w-full justify-center'>
                                        <Link href={`http://${prefix()}accounts.workpeg.com/register?peg=developers`} className='!font-geist w-fit py-[10px] px-[18px] rounded-full bg-[#3383FF] text-[#ffffff] hover:bg-[#5497ff] text-[14px] leading-[20px]'>
                                            Start building
                                        </Link>
                                        <Link target='_blank' href='/docs/latest' className='!font-geist w-fit h-[40px] grid items-center px-[18px] rounded-full text-[#B0B0B0] border-[#1C1C1C] hover:border-[#3B3B3B] border-[1px] text-[14px] leading-[20px]'>
                                            Read the docs
                                        </Link>
                                    </div>
                                    {/* <LandingPageButton onClick={() => router.push(`http://${prefix()}accounts.workpeg.com/login?peg=developers`)}>Get Started</LandingPageButton> */}
                                </div>
                            </div>
                        </LandingPageContainer>
                    </div>
                </HeroBackground>
            </div>

            {/* everything you need section */}
            <LandingPageContainer id='features' additionalclasses='my-[64px] lg:my-[108px] scroll-mt-[100px]'>
                <div className='grid gap-[48px] lg:gap-0'>
                    <div className='max-w-[60%] lg:max-w-full mx-auto lg:mx-0 grid lg:flex gap-[32px] justify-center lg:justify-between items-center lg:py-[64px] lg:px-[48px] lg:bg-[#1C1C1C4D] lg:border-t lg:border-x border-[#1c1c1c]'>
                        <div className='flex flex-col w-full lg:w-fit gap-[16px] lg:gap-[12px] mx-auto lg:mx-0'>
                            <p className='font-jakarta font-medium lg:font-semibold text-[28px] lg:text-[36px] leading-[36px] lg:leading-[44px] tracking-[-0.72px] text-white text-center lg:text-start'>
                                Everything You Need to Build
                            </p>
                            <LandingSubText additionalclasses='lg:!text-start lg:max-w-full'>
                                Each of these is a full section in the documentation
                            </LandingSubText>
                        </div>
                        <Link target='_blank' href='/docs/latest' className='mx-auto lg:mx-0 !font-geist w-fit h-[40px] grid items-center px-[18px] rounded-full text-[#B0B0B0] border-[#1C1C1C] hover:border-[#3B3B3B] border-[1px] text-[14px] leading-[20px]'>
                            Read the docs
                        </Link>
                    </div>
                    <div className='grid lg:grid-cols-3'>
                        {everythingYouNeedData.map((item) => (
                            <Link target='_blank' href={item.url} key={item.id} className={`grid gap-[64px] content-between p-[32px] border-t border-r border-[#1c1c1c] hover:bg-[#1C1C1C33] ${[1, 4, 7].includes(item.id) ? 'border-l border-[#1c1c1c]' : 'border-l-[1px] lg:border-l-[0px]'} ${item.id === 9 ? 'border-b border-[#1c1c1c]' : [7, 8].includes(item.id) ? 'border-[#1c1c1c] border-b-0 lg:border-b' : ''}`}>
                                <div className='text-[#666666]'>
                                    {item.icon}
                                </div>
                                <div className='grid gap-[8px]'>
                                    <p className='font-medium text-[16px] leading-[24px] font-geist text-[#ffffff]'>{item.title}</p>
                                    <p className='font-geist text-[16px] leading-[26px] text-[#858585]'>{item.description}</p>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </LandingPageContainer>

            {/* sdk overview section */}
            <LandingPageContainer id='sdk-overview' additionalclasses='scroll-mt-[100px]'>
                <div className='bg-[url(/images/sdk-bg-overlay.png)] bg-center bg-no-repeat bg-cover bg-[#1C1C1C4D] border border-[#0D0D0D] px-[16px] lg:px-[48px] py-[64px] lg:py-[48px] w-full flex flex-col lg:flex-row justify-between gap-[64px] lg:gap-[112px]'>
                    <div className='grid gap-[32px] lg:gap-[99.92px] content-between'>
                        <div className='grid gap-[16px] lg:gap-[24px] max-w-[85%] lg:max-w-full mx-auto lg:mx-0'>
                            <p className='font-jakarta text-center lg:text-start font-semibold text-[36px] leading-[44px] tracking-[-0.72px] text-white'>SDK Overview</p>
                            <LandingSubText additionalclasses='max-w-[400px] lg:text-start'>
                                Create custom functions, automate workflows, build internal tools, and publish Pegs, all on Workpeg&apos;s AI native execution engine.
                            </LandingSubText>
                        </div>
                        <div className="flex flex-wrap justify-center lg:justify-start items-center gap-[12px]">
                            <Link target='_blank' href='#' className='!font-geist w-fit py-[10px] px-[18px] rounded-full bg-[#3383FF] text-[#ffffff] hover:bg-[#5497ff] text-[14px] leading-[20px]'>
                                Install SDK
                            </Link>
                            <Link target='_blank' href='/docs/latest' className='!font-geist w-fit h-[40px] grid items-center px-[18px] rounded-full text-[#B0B0B0] border-[#1C1C1C] hover:border-[#3B3B3B] border-[1px] text-[14px] leading-[20px]'>
                                Documentation
                            </Link>
                            <Link target='_blank' href='https://gitlab.com/workpeg/workpeg-sdk' className='font-geist w-fit h-[40px] flex gap-[8px] items-center px-[18px] rounded-full text-[#B0B0B0] hover:bg-[#2E2E2E] hover:text-[#ffffff] text-[14px] leading-[20px] '>
                                <IconBrandGitlab stroke={1.5} size={18} />
                                GitLab
                            </Link>
                        </div>
                    </div>
                    <div className='w-full lg:flex-1 lg:max-w-[562px] rounded-2xl border border-[#1C1C1C] bg-[#0A0A0A] overflow-hidden self-center'>
                        <div className='flex items-center gap-[16px] px-[20px] py-[14px] border-b border-[#1C1C1C] bg-[#1C1C1C4D]'>
                            <div className='flex items-center gap-[6px]'>
                                <span className='w-[9px] h-[9px] rounded-full bg-[#333333]' />
                                <span className='w-[9px] h-[9px] rounded-full bg-[#333333]' />
                                <span className='w-[9px] h-[9px] rounded-full bg-[#333333]' />
                            </div>
                            <p className='font-mono text-[13px] text-[#666666]'>pages/counter.py</p>
                        </div>
                        <div className='px-[20px] py-[24px] lg:py-[32px] overflow-x-auto'>
                            <pre className='font-mono text-[13px] leading-[22px] whitespace-pre'>
                                {codePreviewLines.map((line, lineIndex) => (
                                    <div key={lineIndex}>
                                        {line.length === 0
                                            ? ' '
                                            : line.map((segment, segmentIndex) => (
                                                <span key={segmentIndex} className={codeTokenColors[segment.color]}>{segment.text}</span>
                                            ))}
                                    </div>
                                ))}
                            </pre>
                        </div>
                    </div>
                </div>
            </LandingPageContainer>

            {/* installation steps section */}
            <LandingPageContainer id='quickstart' additionalclasses='my-[64px] lg:my-[108px] scroll-mt-[100px]'>
                <div className='grid gap-[24px] lg:border border-[#1c1c1c] lg:px-[24px] lg:py-[32px]'>
                    <div className='max-w-[50%] lg:max-w-full mx-auto lg:mx-0 grid lg:flex gap-[32px] justify-center lg:justify-between items-center pt-[32px] lg:pt-[16px] pb-[32px] lg:px-[24px]'>
                        <p className='font-jakarta font-medium lg:font-semibold text-[28px] lg:text-[36px] leading-[36px] lg:leading-[44px] tracking-[-0.72px] text-white text-center lg:text-start'>
                            From Install to a Running Peg,<br /> in Four Steps
                        </p>
                        <Link href={`http://${prefix()}accounts.workpeg.com/register?peg=developers`} className='!font-geist py-[8px] px-[16px] rounded-full text-[#000000] hover:bg-[#FAFAFA] bg-[#FFFFFF] !font-geist w-fit h-[40px] grid items-center px-[18px] rounded-full text-[#000000] text-[14px] leading-[20px] mx-auto lg:mx-0'>
                            Start building
                        </Link>
                    </div>
                    {/* mobile: stacked list */}
                    <div className='grid gap-[16px] lg:hidden'>
                        {installationsteps.map((item) => {
                            const isActive = activeStep === item.id;
                            return (
                                <div
                                    key={item.id}
                                    onClick={() => setActiveStep(item.id)}
                                    className={`rounded-[16px] border  ${isActive ? 'border-[#1c1c1c] p-[8px] gap-[12px]' : 'border-[#0d0d0d]'}`}
                                >
                                    <div className='flex items-center justify-between gap-[16px] bg-[#1C1C1C4D] rounded-[12px] py-[18.19px] px-[16px]'>
                                        <span className='font-jakarta text-[48px] leading-[60px] font-bold text-[#2E2E2E]'>{item.step}</span>
                                        <p className='font-geist text-[16px] leading-[24px] text-white font-medium'>{item.title}</p>
                                    </div>
                                    <div className={`grid transition-[grid-template-rows] duration-500 ease-in-out ${isActive ? 'grid-rows-[1fr] p-[12px]' : 'grid-rows-[0fr] mt-0'}`}>
                                        <div className='overflow-hidden min-h-0'>
                                            <div className={`grid gap-[48px] transition-opacity duration-500 ease-in-out ${isActive ? 'opacity-100' : 'opacity-0'}`}>
                                                <p className='font-geist text-[16px] leading-[24px] text-[#858585]'>{item.description}</p>
                                                {item.cta ? (
                                                    <Link href={`http://${prefix()}accounts.workpeg.com/register?peg=developers`} className='!font-geist py-[8px] px-[16px] rounded-full text-[#B0B0B0] border-[#1C1C1C] hover:border-[#3B3B3B] border-[1px] text-[14px] leading-[20px] w-fit whitespace-nowrap'>
                                                        Start building
                                                    </Link>
                                                ) : (
                                                    <span className='flex gap-[1px] items-center justify-between text-[#ffffff] w-full text-[12px] leading-[16px]'>
                                                        <p className='bg-[#1C1C1C] py-[10px] pl-[12px] rounded-l-[8px] w-full' style={{ fontFamily: 'Geist Mono, monospace' }}>{item.command}</p>
                                                        <div className='grid place-items-center p-[9px] rounded-r-[8px] bg-[#1C1C1C]  hover:bg-[#2E2E2E] transition-colors duration-300 ease-in-out'>
                                                            {
                                                                copiedCommand === item.id ? (
                                                                    <IconCheck size={18} />
                                                                ) : (
                                                                    <IconCopy size={18} onClick={() => handleCopyCommand(item.command, item.id)} className="cursor-pointer" />
                                                                )
                                                            }
                                                        </div>
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* desktop: interactive row, active step expands on hover */}
                    <div className='hidden lg:flex gap-[16px]'>
                        {installationsteps.map((item) => {
                            const isActive = activeStep === item.id;
                            return (
                                <div
                                    key={item.id}
                                    onMouseEnter={() => setActiveStep(item.id)}
                                    style={{ width: isActive ? 'calc(100% - 588px)' : '180px' }}
                                    className={`flex shrink-0 rounded-[16px] overflow-hidden transition-[width] border-[1px] duration-500 ease-in-out 
                                    ${isActive ? 'border-[#1c1c1c]' : 'border-[#0d0d0d]'}`}
                                >
                                    <div className={`shrink-0 grid content-between rounded-[12px] p-[24px] bg-[#1C1C1C4D] transition-all duration-500 ease-in-out w-[180px] h-[287px] ${isActive ? 'scale-x-[0.911] scale-y-[0.944] w-[199px]' : 'w-[180px]'}`}>
                                        <span className='font-jakarta font-bold text-[48px] leading-[60px] text-[#2E2E2E] tracking-[-0.96px]'>{item.step}</span>
                                        <p className='font-geist text-[16px] leading-[24px] text-white font-medium'>{item.title}</p>
                                    </div>
                                    <div
                                        className={`flex flex-col w-full justify-between transition-[opacity,padding] duration-500 ease-in-out overflow-hidden  h-[287px] ${isActive ? 'opacity-100 pl-[16px] pr-[24px] py-[24px]' : 'opacity-0 p-0'}`}
                                    >
                                        <p className='font-geist text-[16px] leading-[24px] text-[#858585] whitespace-normal'>{item.description}</p>
                                        {item.cta ? (
                                            <Link href={`http://${prefix()}accounts.workpeg.com/register?peg=developers`} className='!font-geist py-[8px] px-[16px] rounded-full text-[#B0B0B0] border-[#1C1C1C] hover:border-[#3B3B3B] border-[1px] text-[14px] leading-[20px] w-fit whitespace-nowrap'>
                                                Start building
                                            </Link>
                                        ) : (
                                            <span className='flex gap-[1px] items-center justify-between text-[#ffffff] w-full text-[12px] leading-[16px]'>
                                                <p className='bg-[#1C1C1C] py-[10px] pl-[12px] rounded-l-[8px] w-full' style={{ fontFamily: 'Geist Mono, monospace' }}>{item.command}</p>
                                                <div className='grid place-items-center p-[9px] rounded-r-[8px] bg-[#1C1C1C]  hover:bg-[#2E2E2E] transition-colors duration-300 ease-in-out'>
                                                    {
                                                        copiedCommand === item.id ? (
                                                            <IconCheck size={18} />
                                                        ) : (
                                                            <IconCopy size={18} onClick={() => handleCopyCommand(item.command, item.id)} className="cursor-pointer" />
                                                        )
                                                    }
                                                </div>
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </LandingPageContainer>
            <LandingPageContainer>
                <div className='bg-[url(/images/footer-bg-overlay.png)] bg-center bg-no-repeat bg-cover'>
                    <div className="border-x border-t border-[#1c1c1c] py-[132px] grid gap-[32px] justify-items-center w-full bg-[url('/images/footer-bg.webp')] bg-cover bg-center bg-no-repeat">
                        <p className="font-jakarta text-[36px] lg:text-[60px] leading-[44px] lg:leading-[72px] max-w-[541px] mx-auto tracking-[-0.72px] lg:tracking-[1.2px] text-[#f5f5f5] font-semibold mx-auto text-center">
                            Ship faster with Workpeg
                        </p>
                        <div className='flex items-center gap-[12px] justify-center'>
                            <Link href={`http://${prefix()}accounts.workpeg.com/register?peg=developers`} className='!font-geist w-fit h-[40px] grid items-center px-[18px] rounded-full text-[#000000] hover:bg-[#FAFAFA] bg-[#FFFFFF] !font-geist w-fit h-[40px] grid items-center px-[18px] rounded-full text-[#000000] text-[14px] leading-[20px] mx-auto lg:mx-0'>
                                Start building
                            </Link>
                            <Link href='/docs/latest' className='!font-geist w-fit h-[40px] grid items-center px-[18px] rounded-full text-[#B0B0B0] border-[#1C1C1C] hover:border-[#3B3B3B] border-[1px] text-[14px] leading-[20px]'>
                                Read the docs
                            </Link>
                        </div>
                    </div>
                </div>
                <div className="px-[24px] lg:px-[48px] py-[48px] grid lg:flex gap-[64px] items-center justify-between border-x border-t border-[#1C1C1C]">
                    <div className="grid gap-[32px]">
                        <img src="https://cdn.workpeg.com/pegs/home/assets/logo/secondary-dark.svg" className="h-[24px]" />
                        <div className="lg:hidden flex items-center gap-[24px] text-[#757575]">
                            <a href="https://www.linkedin.com/company/workpeginc/" target="_blank"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-brand-linkedin"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M8 11v5" /><path d="M8 8v.01" /><path d="M12 16v-5" /><path d="M16 16v-3a2 2 0 1 0 -4 0" /><path d="M3 7a4 4 0 0 1 4 -4h10a4 4 0 0 1 4 4v10a4 4 0 0 1 -4 4h-10a4 4 0 0 1 -4 -4l0 -10" /></svg></a>
                            <a href="https://www.instagram.com/workpeghq/" target="_blank"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-brand-instagram"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M4 8a4 4 0 0 1 4 -4h8a4 4 0 0 1 4 4v8a4 4 0 0 1 -4 4h-8a4 4 0 0 1 -4 -4l0 -8" /><path d="M9 12a3 3 0 1 0 6 0a3 3 0 0 0 -6 0" /><path d="M16.5 7.5v.01" /></svg></a>
                            <a href="https://x.com/workpeg" target="_blank"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-brand-x"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M4 4l11.733 16h4.267l-11.733 -16l-4.267 0" /><path d="M4 20l6.768 -6.768m2.46 -2.46l6.772 -6.772" /></svg></a>
                        </div>
                    </div>
                    <ul className="grid lg:flex items-center gap-[32px] lg:gap-[48px]">
                        <li className="text-[#858585] transition-all duration-300 text-[16px] leading-[24px] hover:text-[#FDFDFD]" data-i18n="header.nav.apps"><a target='_blank' href="https://workpeg.com/">Main Website</a></li>
                        <li className="text-[#858585] transition-all duration-300 text-[16px] leading-[24px] hover:text-[#FDFDFD]" data-i18n="header.nav.apps"><a target='_blank' href="https://workpeg.com/about-us.html">About</a></li>
                        <li className="text-[#858585] transition-all duration-300 text-[16px] leading-[24px] hover:text-[#FDFDFD]" data-i18n="header.nav.apps"><a target="_blank" href="mailto:careers@workpeg.com">Careers</a></li>
                        <li className="text-[#858585] transition-all duration-300 text-[16px] leading-[24px] hover:text-[#FDFDFD]" data-i18n="header.nav.apps"><a target="_blank" href="https://accounts.workpeg.com/terms-and-conditions">Privacy Policy</a></li>
                    </ul>
                </div>
                <div className="px-[24px] lg:px-[48px] py-[48px] flex items-center justify-between border-[1px] border-[#1C1C1C] mb-[56px]">
                    <p className="text-[14px] leading-[20px] text-[#67696E]">Copyright © 2026 Workpeg. All rights reserved.</p>
                    <div className="hidden lg:flex items-center gap-[24px] text-[#757575]">
                        <a href="https://www.linkedin.com/company/workpeginc/" target="_blank"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-brand-linkedin"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M8 11v5" /><path d="M8 8v.01" /><path d="M12 16v-5" /><path d="M16 16v-3a2 2 0 1 0 -4 0" /><path d="M3 7a4 4 0 0 1 4 -4h10a4 4 0 0 1 4 4v10a4 4 0 0 1 -4 4h-10a4 4 0 0 1 -4 -4l0 -10" /></svg></a>
                        <a href="https://www.instagram.com/workpeghq/" target="_blank"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-brand-instagram"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M4 8a4 4 0 0 1 4 -4h8a4 4 0 0 1 4 4v8a4 4 0 0 1 -4 4h-8a4 4 0 0 1 -4 -4l0 -8" /><path d="M9 12a3 3 0 1 0 6 0a3 3 0 0 0 -6 0" /><path d="M16.5 7.5v.01" /></svg></a>
                        <a href="https://x.com/workpeg" target="_blank"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-brand-x"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M4 4l11.733 16h4.267l-11.733 -16l-4.267 0" /><path d="M4 20l6.768 -6.768m2.46 -2.46l6.772 -6.772" /></svg></a>
                    </div>
                </div>
            </LandingPageContainer>
        </div >
    );
};

export default LandingPage;